/**
 * BulkAssetSwapModal — Find and replace an asset across all rows in the current match.
 *
 * When a sponsor updates their creative mid-season, clubs can swap the old asset
 * for the new one across every row in one action instead of editing row-by-row.
 */
import React, { useState, useMemo, useCallback } from "react";

const ASSET_FIELDS = [
  { key: "bigGfxVideosId",       label: "CG / Big Screen" },
  { key: "cornerGfxVideosId",    label: "Corner Screen" },
  { key: "ledAllTypesId",        label: "LED - All Types" },
  { key: "bigTvcId",             label: "Big Screen TVC" },
  { key: "cornerTvcId",          label: "Corner TVC" },
  { key: "iptvGfxId",            label: "IPTV GFX" },
  { key: "mcgTiledLedId",        label: "MCG Tiled LED" },
  { key: "marvelTiledLedId",     label: "Marvel Tiled LED" },
  { key: "superFullId",          label: "Super Full" },
  { key: "super169Id",           label: "Super 16:9" },
  { key: "superSideId",          label: "Super Side" },
  { key: "mcgInterchangeId",     label: "MCG Interchange" },
  { key: "marvelInterchangeId",  label: "Marvel Interchange" },
  { key: "marvel360Id",          label: "Marvel 360" },
  { key: "mcg360Id",             label: "MCG 360" },
];

/**
 * Build a deduplicated list of assets currently used across all items.
 * Returns [{ id, title, lumenId, fieldKey, fieldLabel, usedCount }]
 */
function buildAssetList(items, assetCache) {
  const map = new Map(); // assetId → { ...info, fields: Set, count }

  for (const item of items) {
    for (const { key, label } of ASSET_FIELDS) {
      const assetId = item[key];
      if (!assetId) continue;

      if (!map.has(assetId)) {
        const cached = assetCache.get(assetId);
        map.set(assetId, {
          id: assetId,
          title: cached?.title || cached?.name || "",
          lumenId: cached?.lumenId || "",
          thumbnail: cached?.thumbnailUrl || cached?.thumbnail || null,
          fields: new Set(),
          count: 0,
        });
      }
      const entry = map.get(assetId);
      entry.fields.add(label);
      entry.count++;
    }
  }

  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

export default function BulkAssetSwapModal({
  isOpen,
  onClose,
  items,          // Current match items
  assetCache,     // Asset cache for displaying names/thumbnails
  api,            // API adapter for updates
  onComplete,     // Callback after swap (triggers reload)
}) {
  const [findAssetId, setFindAssetId] = useState("");
  const [replaceAssetId, setReplaceAssetId] = useState("");
  const [replaceSearch, setReplaceSearch] = useState("");
  const [swapping, setSwapping] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);

  // Build list of assets currently in use
  const assetList = useMemo(() => buildAssetList(items, assetCache), [items, assetCache]);

  // Find affected rows when a "find" asset is selected
  const affectedRows = useMemo(() => {
    if (!findAssetId) return [];
    return items.filter(item => {
      return ASSET_FIELDS.some(({ key }) => item[key] === findAssetId);
    });
  }, [items, findAssetId]);

  // Filter replacement asset search
  const filteredReplacementAssets = useMemo(() => {
    if (!replaceSearch.trim()) return assetList.slice(0, 20);
    const term = replaceSearch.toLowerCase();
    return assetList.filter(a =>
      a.title.toLowerCase().includes(term) ||
      a.lumenId.toLowerCase().includes(term) ||
      a.id.toLowerCase().includes(term)
    ).slice(0, 20);
  }, [assetList, replaceSearch]);

  // Get asset display info
  const getAssetLabel = useCallback((assetId) => {
    if (!assetId) return "";
    const cached = assetCache.get(assetId);
    if (cached) {
      return cached.lumenId || cached.title || cached.name || assetId;
    }
    return assetId;
  }, [assetCache]);

  if (!isOpen) return null;

  async function handleSwap() {
    if (!findAssetId || !replaceAssetId || swapping) return;
    if (findAssetId === replaceAssetId) return;

    setSwapping(true);
    setResult(null);
    setProgress({ done: 0, total: affectedRows.length });

    let success = 0;
    let errors = 0;

    try {
      for (const item of affectedRows) {
        try {
          // Build patch — swap only the fields that match the findAssetId
          const patch = {};
          for (const { key } of ASSET_FIELDS) {
            if (item[key] === findAssetId) {
              patch[key] = replaceAssetId;
            }
          }

          if (Object.keys(patch).length > 0) {
            await api.update(item.id, patch);
            success++;
          }
        } catch (err) {
          console.error("[BulkSwap] Failed to update row:", item.name, err);
          errors++;
        }

        setProgress(prev => ({ ...prev, done: prev.done + 1 }));
      }

      setResult({ success, errors });
      if (onComplete) onComplete();
    } catch (err) {
      console.error("[BulkSwap] Swap failed:", err);
      setResult({ success: 0, errors: 1, message: "Swap failed: " + (err.message || err) });
    } finally {
      setSwapping(false);
    }
  }

  function handleClose() {
    if (swapping) return;
    setFindAssetId("");
    setReplaceAssetId("");
    setReplaceSearch("");
    setResult(null);
    setProgress({ done: 0, total: 0 });
    onClose();
  }

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  const assetPillStyle = (isSelected) => ({
    display: "flex", alignItems: "center", gap: 8,
    padding: "8px 12px", borderRadius: 8, cursor: "pointer",
    background: isSelected ? "rgba(59,130,246,0.15)" : "rgba(255,255,255,0.04)",
    border: `1px solid ${isSelected ? "rgba(59,130,246,0.5)" : "rgba(255,255,255,0.1)"}`,
    transition: "all 0.15s ease",
  });

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
    }} onClick={swapping ? undefined : handleClose}>
      <div style={{
        background: "#1a1a1d", borderRadius: 16, padding: "28px 32px",
        minWidth: 520, maxWidth: 620, width: "90%",
        maxHeight: "85vh", overflowY: "auto",
        border: "1px solid rgba(255,255,255,0.1)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <span style={{ fontSize: 22 }}>🔄</span>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#f8fafc" }}>
              Bulk Asset Swap
            </h2>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#8a8a96" }}>
              Replace an asset across all rows in this match
            </p>
          </div>
        </div>

        {/* Step 1: Find Asset */}
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#cbd5e1", marginBottom: 6 }}>
          1. Select asset to replace
        </label>
        <div style={{
          maxHeight: 180, overflowY: "auto", borderRadius: 8,
          border: "1px solid rgba(255,255,255,0.1)", marginBottom: 16,
          background: "#111114", padding: 6,
        }}>
          {assetList.length === 0 ? (
            <div style={{ padding: 16, textAlign: "center", color: "#6b6b78", fontSize: 12 }}>
              No assets allocated in this match
            </div>
          ) : (
            assetList.map(asset => (
              <div
                key={asset.id}
                onClick={() => { if (!swapping) setFindAssetId(asset.id); }}
                style={assetPillStyle(findAssetId === asset.id)}
              >
                {asset.thumbnail && (
                  <img src={asset.thumbnail} alt="" style={{
                    width: 32, height: 32, borderRadius: 4, objectFit: "cover",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {asset.title || asset.lumenId || asset.id}
                  </div>
                  <div style={{ fontSize: 10, color: "#6b6b78" }}>
                    {asset.lumenId && asset.title ? asset.lumenId + " · " : ""}
                    Used in {asset.count} row{asset.count !== 1 ? "s" : ""} · {[...asset.fields].join(", ")}
                  </div>
                </div>
                {findAssetId === asset.id && (
                  <span style={{ fontSize: 14, color: "#3b82f6" }}>✓</span>
                )}
              </div>
            ))
          )}
        </div>

        {/* Affected rows preview */}
        {findAssetId && affectedRows.length > 0 && (
          <div style={{
            padding: "8px 12px", borderRadius: 8, marginBottom: 16,
            background: "rgba(249,115,22,0.08)", borderLeft: "3px solid #f97316",
            fontSize: 11, color: "#fb923c", lineHeight: 1.5,
          }}>
            <strong>{affectedRows.length} row{affectedRows.length !== 1 ? "s" : ""} affected:</strong>{" "}
            {affectedRows.slice(0, 6).map(r => r.name || "Unnamed").join(", ")}
            {affectedRows.length > 6 ? ` +${affectedRows.length - 6} more` : ""}
          </div>
        )}

        {/* Step 2: Replace With */}
        {findAssetId && (
          <>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#cbd5e1", marginBottom: 6 }}>
              2. Replace with
            </label>
            <input
              type="text"
              value={replaceSearch}
              onChange={e => setReplaceSearch(e.target.value)}
              placeholder="Search assets by name or Lumen8 ID…"
              disabled={swapping}
              style={{
                width: "100%", padding: "8px 12px", fontSize: 12,
                borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)",
                background: "#111114", color: "#f8fafc", marginBottom: 8,
                boxSizing: "border-box",
              }}
            />
            <div style={{
              maxHeight: 160, overflowY: "auto", borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.1)", marginBottom: 16,
              background: "#111114", padding: 6,
            }}>
              {filteredReplacementAssets.filter(a => a.id !== findAssetId).map(asset => (
                <div
                  key={asset.id}
                  onClick={() => { if (!swapping) setReplaceAssetId(asset.id); }}
                  style={assetPillStyle(replaceAssetId === asset.id)}
                >
                  {asset.thumbnail && (
                    <img src={asset.thumbnail} alt="" style={{
                      width: 28, height: 28, borderRadius: 4, objectFit: "cover",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {asset.title || asset.lumenId || asset.id}
                    </div>
                    <div style={{ fontSize: 10, color: "#6b6b78" }}>
                      {asset.lumenId || asset.id}
                    </div>
                  </div>
                  {replaceAssetId === asset.id && (
                    <span style={{ fontSize: 14, color: "#22c55e" }}>✓</span>
                  )}
                </div>
              ))}
              {filteredReplacementAssets.filter(a => a.id !== findAssetId).length === 0 && (
                <div style={{ padding: 12, textAlign: "center", color: "#6b6b78", fontSize: 11 }}>
                  No matching assets found
                </div>
              )}
            </div>
          </>
        )}

        {/* Summary */}
        {findAssetId && replaceAssetId && !result && (
          <div style={{
            padding: "10px 14px", borderRadius: 8, marginBottom: 16,
            background: "rgba(34,197,94,0.08)", borderLeft: "3px solid #22c55e",
            fontSize: 12, color: "#86efac", lineHeight: 1.6,
          }}>
            Replace <strong style={{ color: "#f87171" }}>{getAssetLabel(findAssetId)}</strong>
            {" → "}
            <strong style={{ color: "#22c55e" }}>{getAssetLabel(replaceAssetId)}</strong>
            {" "}across <strong>{affectedRows.length} row{affectedRows.length !== 1 ? "s" : ""}</strong>
          </div>
        )}

        {/* Progress */}
        {swapping && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#8a8a96", marginBottom: 4 }}>
              <span>Swapping assets…</span>
              <span>{progress.done} / {progress.total}</span>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: "#2a2a30", overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: 3,
                width: `${pct}%`,
                background: "linear-gradient(90deg, #3b82f6, #22c55e)",
                transition: "width 0.3s ease",
              }} />
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div style={{
            padding: "12px 14px", borderRadius: 8, marginBottom: 16,
            background: result.errors > 0 ? "rgba(239,68,68,0.1)" : "rgba(34,197,94,0.1)",
            borderLeft: `3px solid ${result.errors > 0 ? "#ef4444" : "#22c55e"}`,
            fontSize: 12, color: result.errors > 0 ? "#fca5a5" : "#86efac", lineHeight: 1.5,
          }}>
            {result.message ? result.message : (
              <>
                <strong>{result.success} row{result.success !== 1 ? "s" : ""} updated successfully.</strong>
                {result.errors > 0 && ` ${result.errors} failed.`}
              </>
            )}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={handleClose}
            disabled={swapping}
            style={{
              padding: "9px 20px", fontSize: 13, fontWeight: 600, borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.15)", background: "transparent",
              color: "#94a3b8", cursor: swapping ? "not-allowed" : "pointer",
            }}
          >
            {result ? "Close" : "Cancel"}
          </button>
          {!result && (
            <button
              onClick={handleSwap}
              disabled={!findAssetId || !replaceAssetId || swapping || findAssetId === replaceAssetId}
              style={{
                padding: "9px 24px", fontSize: 13, fontWeight: 700, borderRadius: 8,
                border: "none",
                background: (!findAssetId || !replaceAssetId || swapping) ? "#2a2a30" : "linear-gradient(135deg, #3b82f6, #6366f1)",
                color: (!findAssetId || !replaceAssetId || swapping) ? "#6b6b78" : "#fff",
                cursor: (!findAssetId || !replaceAssetId || swapping) ? "not-allowed" : "pointer",
                boxShadow: (findAssetId && replaceAssetId && !swapping) ? "0 2px 10px rgba(59,130,246,0.3)" : "none",
              }}
            >
              {swapping ? `Swapping… ${pct}%` : `Swap in ${affectedRows.length} Row${affectedRows.length !== 1 ? "s" : ""}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
